// src/features/photoface/components/GuestSearch.jsx
// صفحة عامة لضيوف المناسبة — يفتحها رمز QR: /search/:albumId?token=...
// البحث بالسيلفي يذهب مباشرة إلى PhotoFace (نقطة عامة)، والنتائج تُحفظ في المتصفح
// بحيث يستعيد الضيف صوره لاحقاً دون سيلفي جديد، والتنزيل يمر عبر سيرفرنا بترويسة تنزيل.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import styles from './GuestSearch.module.css';

const PF_API = 'https://facesearch.space/api';
const GT_API = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');

const storageKey = (albumId) => `pf_guest_${albumId}`;

const readSaved = (albumId) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(albumId)) || 'null');
  } catch {
    return null;
  }
};

const writeSaved = (albumId, data) => {
  try {
    localStorage.setItem(storageKey(albumId), JSON.stringify(data));
  } catch { /* تجاهل — قد يكون التخزين ممتلئاً */ }
};

/** استخراج مفتاح S3 من رابط presigned */
const keyFromUrl = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.replace(/^\//, ''));
  } catch {
    return '';
  }
};

const SELFIE_SLOTS = [
  { field: 'front', label: 'الوجه أماماً (إلزامي)', icon: '🙂' },
  { field: 'right', label: 'التفاتة يمين (اختياري)', icon: '👉' },
  { field: 'left',  label: 'التفاتة يسار (اختياري)', icon: '👈' },
];

// خطوات الكاميرا الحية — ثلاث لقطات موجهة تحسّن دقة المطابقة
const SHOT_STEPS = {
  front: { icon: '🙂', label: 'ضع وجهك داخل الإطار وانظر مباشرة إلى الكاميرا' },
  right: { icon: '👉', label: 'ممتاز! الآن أدر وجهك قليلاً نحو اليمين' },
  left:  { icon: '👈', label: 'وأخيراً أدر وجهك قليلاً نحو اليسار' },
};
const SHOT_SHORT = { front: 'أمام', right: 'يمين', left: 'يسار' };

const GuestSearch = () => {
  const { albumId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const requestParam = searchParams.get('request') || ''; // رابط موافقة يدوية من المصور

  const [selfies, setSelfies] = useState({ front: null, right: null, left: null });
  const [previews, setPreviews] = useState({});
  const [phase, setPhase] = useState('capture'); // capture | searching | results | pending | rejected
  const [results, setResults] = useState([]);    // [{s3_key, url}]
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [reqSecret, setReqSecret] = useState(''); // سر طلب المراجعة — بديل للتوكن في التنزيل
  const [visibleCount, setVisibleCount] = useState(6); // بيجنيشن عرض النتائج — 6 أولاً ثم "عرض المزيد"

  const inputRefs = useRef({});
  const pollRef = useRef(null);

  // ── الكاميرا الحية ──
  const [camState, setCamState] = useState('idle'); // idle | starting | on | denied
  const [shotStep, setShotStep] = useState('front');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamState('idle');
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]); // إيقاف عند مغادرة الصفحة

  useEffect(() => { setVisibleCount(6); }, [results]); // نتائج جديدة → نعود لأول 6

  useEffect(() => {
    if (camState === 'on' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [camState]);

  const startCamera = async () => {
    setError('');
    setCamState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setShotStep('front');
      setCamState('on');
    } catch {
      setCamState('denied');
      setError('تعذر فتح الكاميرا — يمكنك رفع الصور من جهازك بالأسفل.');
    }
  };

  // ── استعادة النتائج المحفوظة عبر باكند الكراجويشن (روابط جديدة) ──
  const restoreSaved = useCallback(async (saved) => {
    setRestoring(true);
    setError('');
    try {
      const res = await fetch(`${GT_API}/photoface-guest/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          album_id: albumId,
          token,
          request_secret: saved.requestSecret || undefined,
          s3_keys: saved.keys,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'تعذر استعادة الصور');
      if (data.photos?.length) {
        if (saved.requestSecret) setReqSecret(saved.requestSecret);
        setResults(data.photos);
        setPhase('results');
      } else {
        setError('لم نعثر على صورك المحفوظة — ابحث من جديد.');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRestoring(false);
    }
  }, [albumId, token]);

  // ── متابعة طلب المراجعة اليدوية ──
  const checkRequest = useCallback(async (secret) => {
    try {
      const res = await fetch(`${PF_API}/albums/check-request/${secret}/`);
      const data = await res.json();
      if (data.status === 'COMPLETED') {
        clearInterval(pollRef.current);
        const photos = (data.photos || []).map((url) => ({ s3_key: keyFromUrl(url), url }));
        setReqSecret(secret);
        setResults(photos);
        setPhase('results');
        writeSaved(albumId, {
          token,
          requestSecret: secret,
          keys: photos.map((p) => p.s3_key),
          savedAt: Date.now(),
        });
      } else if (data.status === 'REJECTED') {
        clearInterval(pollRef.current);
        setPhase('rejected');
      }
      // PENDING / READY → نبقى في الانتظار
    } catch { /* شبكة — نحاول في الدورة القادمة */ }
  }, [albumId, token]);

  // ── عند فتح الصفحة: رابط موافقة مباشر؟ أو نتائج/طلب محفوظ؟ ──
  useEffect(() => {
    // 1) ضيف قادم من رابط الموافقة الذي أرسله المصور (?request=...)
    if (requestParam) {
      setReqSecret(requestParam);
      setPhase('pending');
      checkRequest(requestParam);
      pollRef.current = setInterval(() => checkRequest(requestParam), 15000);
      return () => clearInterval(pollRef.current);
    }
    // 2) زيارة سابقة محفوظة في هذا المتصفح
    const saved = readSaved(albumId);
    if (!saved || saved.token !== token) return undefined;
    if (saved.requestSecret && !saved.keys?.length) {
      setPhase('pending');
      checkRequest(saved.requestSecret);
      pollRef.current = setInterval(() => checkRequest(saved.requestSecret), 15000);
    } else if (saved.keys?.length) {
      if (saved.requestSecret) setReqSecret(saved.requestSecret);
      setHasSaved(true);
    }
    return () => clearInterval(pollRef.current);
  }, [albumId, token, requestParam, checkRequest]);

  // ── اختيار سيلفي ──
  const handleSelfie = (field, file) => {
    if (!file) return;
    setSelfies((prev) => ({ ...prev, [field]: file }));
    setPreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
  };

  // ── التقاط لقطة من الكاميرا (مضغوطة JPEG بعرض أقصى 720px) ──
  const captureShot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const maxW = 720;
    const scale = Math.min(1, maxW / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const stepNow = shotStep;
    canvas.toBlob((blob) => {
      if (!blob) return;
      handleSelfie(stepNow, new File([blob], `${stepNow}.jpg`, { type: 'image/jpeg' }));
      if (stepNow === 'front') setShotStep('right');
      else if (stepNow === 'right') setShotStep('left');
      else stopCamera(); // اكتملت اللقطات الثلاث
    }, 'image/jpeg', 0.85);
  };

  const skipShot = () => {
    if (shotStep === 'right') setShotStep('left');
    else stopCamera();
  };

  // ── البحث ──
  const handleSearch = async () => {
    if (!selfies.front) { setError('التقط صورة الوجه الأمامية أولاً.'); return; }
    setError('');
    setPhase('searching');

    const form = new FormData();
    form.append('selfie_front', selfies.front);
    if (selfies.right) form.append('selfie_right', selfies.right);
    if (selfies.left)  form.append('selfie_left', selfies.left);
    form.append('token', token);

    try {
      const res = await fetch(`${PF_API}/albums/${albumId}/search_photos/`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'فشل البحث، حاول مرة أخرى.');

      if (data.mode === 'manual') {
        // وضع المراجعة اليدوية — نحفظ المفتاح السري ونتابع
        setReqSecret(data.request_secret);
        writeSaved(albumId, { token, requestSecret: data.request_secret, savedAt: Date.now() });
        setPhase('pending');
        pollRef.current = setInterval(() => checkRequest(data.request_secret), 15000);
        return;
      }

      // الوضع التلقائي — photos_meta (الجديد) أو photos (القديم)
      let photos = [];
      if (Array.isArray(data.photos_meta) && data.photos_meta.length) {
        photos = data.photos_meta.map((p) => ({ s3_key: p.s3_key, url: p.url }));
      } else {
        photos = (data.photos || []).map((url) => ({ s3_key: keyFromUrl(url), url }));
      }

      setResults(photos);
      setPhase('results');
      writeSaved(albumId, { token, keys: photos.map((p) => p.s3_key), savedAt: Date.now() });
    } catch (e) {
      setError(e.message);
      setPhase('capture');
    }
  };

  const downloadHref = (s3Key) =>
    `${GT_API}/photoface-guest/download/?album_id=${albumId}` +
    `&key=${encodeURIComponent(s3Key)}` +
    (reqSecret
      ? `&request_secret=${encodeURIComponent(reqSecret)}`
      : `&token=${encodeURIComponent(token)}`);

  const resetSearch = () => {
    clearInterval(pollRef.current);
    setSelfies({ front: null, right: null, left: null });
    setPreviews({});
    setResults([]);
    setError('');
    setPhase('capture');
  };

  // ════════════════ العرض ════════════════
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>📸 صورك من المناسبة</h1>
        <p className={styles.subtitle}>
          التقط سيلفي وسنعرض لك صورك أنت فقط — تقنية التعرف على الوجه
        </p>

        {error && <div className={styles.errorMsg}>{error}</div>}

        {phase === 'capture' && camState === 'on' && (
          <>
            {/* الكاميرا الحية مع إطار توجيه الوجه */}
            <div className={styles.cameraBox}>
              <video ref={videoRef} className={styles.cameraVideo} autoPlay playsInline muted />
              <div className={`${styles.faceGuide} ${previews[shotStep] ? styles.faceGuideOk : ''}`} />
              <div className={styles.cameraInstruction}>
                {SHOT_STEPS[shotStep].icon} {SHOT_STEPS[shotStep].label}
              </div>
              <div className={styles.cameraControls}>
                {shotStep !== 'front' && (
                  <button type="button" className={styles.smallCtrlBtn} onClick={skipShot}>
                    تخطي
                  </button>
                )}
                <button
                  type="button"
                  className={styles.shutterBtn}
                  onClick={captureShot}
                  aria-label="التقاط الصورة"
                />
                <button type="button" className={styles.smallCtrlBtn} onClick={stopCamera}>
                  إنهاء
                </button>
              </div>
            </div>

            {/* اللقطات الثلاث — اضغط أي واحدة لإعادة التقاطها */}
            <div className={styles.shotsStrip}>
              {SELFIE_SLOTS.map(({ field, icon }) => (
                <div key={field} className={styles.shotCell} onClick={() => setShotStep(field)}>
                  {previews[field]
                    ? <img src={previews[field]} alt="" className={`${styles.shotThumb} ${shotStep === field ? styles.shotActive : ''}`} />
                    : <div className={`${styles.shotEmpty} ${shotStep === field ? styles.shotActive : ''}`}>{icon}</div>}
                  <span className={styles.shotLabel}>{SHOT_SHORT[field]}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={!selfies.front}
              onClick={() => { stopCamera(); handleSearch(); }}
            >
              🔍 ابحث عن صوري
            </button>
            <div className={styles.hint}>اللقطتان الجانبيتان اختياريتان — لكنهما يحسّنان دقة العثور على صورك.</div>
          </>
        )}

        {phase === 'capture' && camState !== 'on' && (
          <>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={camState === 'starting'}
              onClick={startCamera}
            >
              {camState === 'starting' ? '⏳ جاري فتح الكاميرا…' : '📷 افتح الكاميرا والتقط سيلفي'}
            </button>

            {hasSaved && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                disabled={restoring}
                onClick={() => restoreSaved(readSaved(albumId))}
              >
                {restoring ? 'جاري الاستعادة…' : '⟳ استعادة صوري السابقة (بدون سيلفي)'}
              </button>
            )}

            {/* الخيار البديل: رفع/التقاط عبر منتقي الجهاز */}
            <div className={styles.hint}>أو ارفع صوراً من جهازك:</div>
            <div className={styles.selfieRow}>
              {SELFIE_SLOTS.map(({ field, label, icon }) => (
                <div
                  key={field}
                  className={styles.selfieSlot}
                  onClick={() => inputRefs.current[field]?.click()}
                >
                  {previews[field]
                    ? <img src={previews[field]} alt="" className={styles.selfiePreview} />
                    : <div className={styles.selfieEmpty}>{icon}</div>}
                  <span className={styles.selfieLabel}>{label}</span>
                  <input
                    ref={(el) => { inputRefs.current[field] = el; }}
                    type="file"
                    accept="image/*"
                    capture="user"
                    style={{ display: 'none' }}
                    onChange={(e) => handleSelfie(field, e.target.files?.[0])}
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={!selfies.front}
              onClick={handleSearch}
            >
              🔍 ابحث عن صوري
            </button>
          </>
        )}

        {phase === 'searching' && (
          <div className={styles.statusBox}>⏳ جاري البحث عن صورك…</div>
        )}

        {phase === 'pending' && (
          <div className={styles.statusBox}>
            ✅ تم استلام طلبك!<br />
            صورك قيد المراجعة من قبل المصور — يمكنك إغلاق الصفحة
            والعودة لاحقاً <b>بنفس هذا الرابط</b> لعرض صورك بعد الموافقة.
          </div>
        )}

        {phase === 'rejected' && (
          <>
            <div className={styles.statusBox}>😔 نعتذر — لم تتم الموافقة على الطلب.</div>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={resetSearch}>
              محاولة جديدة
            </button>
          </>
        )}

        {phase === 'results' && (
          <>
            <div className={styles.count}>
              {results.length
                ? `وجدنا ${results.length} صورة لك 🎉`
                : 'لم نعثر على صور مطابقة — جرّب سيلفي أوضح.'}
            </div>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={resetSearch}>
              بحث جديد
            </button>
          </>
        )}
      </div>

      {phase === 'results' && results.length > 0 && (
        <>
          <div className={styles.resultsGrid}>
            {results.slice(0, visibleCount).map((p) => (
              <div key={p.s3_key || p.url} className={styles.resultCell}>
                <img src={p.url} alt="" className={styles.resultImg} loading="lazy" />
                {p.s3_key && (
                  <a className={styles.downloadBtn} href={downloadHref(p.s3_key)}>
                    ⬇ تنزيل
                  </a>
                )}
              </div>
            ))}
          </div>
          {visibleCount < results.length && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              style={{ width: 'min(720px, 100%)', marginTop: 12 }}
              onClick={() => setVisibleCount((c) => c + 6)}
            >
              عرض المزيد ({results.length - visibleCount} صورة متبقية)
            </button>
          )}
        </>
      )}

      <p className={styles.hint} style={{ marginTop: 24 }}>
        صورك محفوظة في هذا المتصفح — عد بنفس الرابط في أي وقت خلال صلاحيته.
      </p>
    </div>
  );
};

export default GuestSearch;
